import { useState, useEffect } from 'react'
import { useAuthStore } from '@/stores/authStore'
import { supabase } from '@/lib/supabase'
import { TRACKER_URL } from '@/lib/config'
// PAYMENT DISABLED - Keep for future re-enablement
// import { PRICING } from '@/lib/config'
import { format } from 'date-fns'

const SELF_KEY = 'lurker_self'

export default function SettingsPage() {
  const { profile, sites, refreshSites } = useAuthStore()
  const [isCreatingSite, setIsCreatingSite] = useState(false)
  const [showAddForm, setShowAddForm] = useState(false)
  const [newSite, setNewSite] = useState({ domain: '', name: '' })
  const [error, setError] = useState('')
  const [selectedSiteForCode, setSelectedSiteForCode] = useState<string | null>(null)
  const [isSelfExcluded, setIsSelfExcluded] = useState(false)

  // Check self-exclusion status on mount
  useEffect(() => {
    try {
      setIsSelfExcluded(localStorage.getItem(SELF_KEY) === 'true')
    } catch {
      // localStorage not available
    }
  }, [])

  const toggleSelfExclusion = () => {
    try {
      if (isSelfExcluded) {
        localStorage.removeItem(SELF_KEY)
        setIsSelfExcluded(false)
      } else {
        localStorage.setItem(SELF_KEY, 'true')
        setIsSelfExcluded(true)
      }
    } catch {
      // localStorage not available
    }
  }

  const handleCreateSite = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!profile) return

    setError('')
    setIsCreatingSite(true)

    try {
      const cleanDomain = newSite.domain.replace(/^https?:\/\//, '').replace(/\/$/, '')

      // Check if this domain already exists for this user
      const existingSite = sites.find(s => s.domain === cleanDomain)
      if (existingSite) {
        setError('You already have this domain configured')
        setIsCreatingSite(false)
        return
      }

      const { error: insertError } = await supabase.from('sites').insert({
        user_id: profile.id,
        domain: cleanDomain,
        name: newSite.name,
      } as never)

      if (insertError) {
        if (insertError.code === '23505') {
          setError('This domain is already configured')
        } else {
          setError(insertError.message)
        }
      } else {
        await refreshSites()
        setNewSite({ domain: '', name: '' })
        setShowAddForm(false)
      }
    } catch (err) {
      setError('Failed to create site')
    } finally {
      setIsCreatingSite(false)
    }
  }

  /* PAYMENT DISABLED - Keep for future re-enablement
  const handleManageBilling = async () => {
    try {
      const { data: { session } } = await supabase.auth.getSession()
      if (!session?.access_token) {
        console.error('No session found')
        return
      }

      const response = await fetch('/api/billing/portal', {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${session.access_token}` },
      })
      const { url } = await response.json()
      if (url) {
        window.location.href = url
      }
    } catch (error) {
      console.error('Failed to open billing portal:', error)
    }
  }
  */

  const handleExportData = async () => {
    try {
      const response = await fetch('/api/gdpr/export', { method: 'POST' })
      const blob = await response.blob()
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `data-export-${format(new Date(), 'yyyy-MM-dd')}.json`
      a.click()
      URL.revokeObjectURL(url)
    } catch (error) {
      console.error('Failed to export data:', error)
    }
  }

  const handleDeleteAccount = async () => {
    if (!confirm('Are you sure you want to delete your account? This action cannot be undone.')) {
      return
    }
    if (!confirm('This will permanently delete all your data. Type "DELETE" to confirm.')) {
      return
    }

    try {
      await fetch('/api/gdpr/delete', { method: 'DELETE' })
      await supabase.auth.signOut()
      window.location.href = '/'
    } catch (error) {
      console.error('Failed to delete account:', error)
    }
  }

  const baseUrl = TRACKER_URL || window.location.origin
  const trackerVersion = '2' // Increment to bust cache

  const getTrackerSnippet = (siteKey: string) =>
    `<script async src="${baseUrl}/tracker.js?v=${trackerVersion}" data-site-key="${siteKey}" data-api="${baseUrl}/api/collect"></script>`

  return (
    <div className="max-w-3xl">
      <h1 className="text-2xl font-bold text-gray-900 mb-8">Settings</h1>

      {/* Site Settings */}
      <section className="card mb-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-gray-900">Websites</h2>
          {sites.length > 0 && !showAddForm && (
            <button
              onClick={() => setShowAddForm(true)}
              className="btn-secondary text-sm"
            >
              + Add Site
            </button>
          )}
        </div>

        {/* List of existing sites */}
        {sites.length > 0 && (
          <div className="space-y-4 mb-6">
            {sites.map((s) => {
              const snippet = getTrackerSnippet(s.public_key)
              const isExpanded = selectedSiteForCode === s.id

              return (
                <div key={s.id} className="border border-gray-200 rounded-lg overflow-hidden">
                  <button
                    onClick={() => setSelectedSiteForCode(isExpanded ? null : s.id)}
                    className="w-full px-4 py-3 flex items-center justify-between hover:bg-gray-50 transition-colors"
                  >
                    <div className="text-left">
                      <p className="font-medium text-gray-900">{s.name}</p>
                      <p className="text-sm text-gray-500">{s.domain}</p>
                    </div>
                    <svg
                      className={`w-5 h-5 text-gray-400 transition-transform ${isExpanded ? 'rotate-180' : ''}`}
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </button>

                  {isExpanded && (
                    <div className="px-4 py-4 border-t border-gray-200 bg-gray-50">
                      <div className="grid gap-3 mb-4">
                        <div>
                          <label className="label">Site Key</label>
                          <p className="text-gray-900 font-mono text-sm">{s.public_key}</p>
                        </div>
                      </div>

                      <div>
                        <label className="label">Tracking Code</label>
                        <p className="text-sm text-gray-600 mb-2">
                          Add this code to {s.domain}'s &lt;head&gt; tag:
                        </p>
                        <div className="bg-gray-900 rounded-lg p-4 text-sm text-gray-100 font-mono overflow-x-auto">
                          {snippet}
                        </div>
                        <button
                          onClick={() => navigator.clipboard.writeText(snippet)}
                          className="btn-secondary mt-2"
                        >
                          Copy code
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              )
            })}
          </div>
        )}

        {/* Add site form */}
        {(sites.length === 0 || showAddForm) && (
          <form onSubmit={handleCreateSite} className="space-y-4">
            {showAddForm && (
              <div className="flex items-center justify-between pb-2 border-b border-gray-200 mb-4">
                <h3 className="font-medium text-gray-900">Add New Site</h3>
                <button
                  type="button"
                  onClick={() => {
                    setShowAddForm(false)
                    setError('')
                    setNewSite({ domain: '', name: '' })
                  }}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            )}

            {error && (
              <div className="p-3 text-sm text-red-700 bg-red-50 rounded-lg">
                {error}
              </div>
            )}
            <div>
              <label htmlFor="name" className="label">Site Name</label>
              <input
                id="name"
                type="text"
                required
                value={newSite.name}
                onChange={(e) => setNewSite({ ...newSite, name: e.target.value })}
                placeholder="My Website"
                className="input"
              />
            </div>
            <div>
              <label htmlFor="domain" className="label">Domain</label>
              <input
                id="domain"
                type="text"
                required
                value={newSite.domain}
                onChange={(e) => setNewSite({ ...newSite, domain: e.target.value })}
                placeholder="example.com"
                className="input"
              />
              <p className="text-xs text-gray-500 mt-1">
                Enter your domain without http:// or https://
              </p>
            </div>
            <button type="submit" disabled={isCreatingSite} className="btn-primary">
              {isCreatingSite ? 'Adding...' : 'Add Site'}
            </button>
          </form>
        )}
      </section>

      {/* Usage */}
      <section className="card mb-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Usage This Month</h2>
        <div className="grid gap-4">
          {(() => {
            const quantity = (profile as any)?.subscription_quantity || 1
            const sessionsLimit = 5000 * quantity
            const eventsLimit = 100000 * quantity
            const sessionsUsed = profile?.sessions_count || 0
            const eventsUsed = profile?.events_count || 0
            const sessionsPercent = Math.min(100, (sessionsUsed / sessionsLimit) * 100)
            const eventsPercent = Math.min(100, (eventsUsed / eventsLimit) * 100)

            return (
              <>
                <div>
                  <div className="flex justify-between text-sm mb-1">
                    <span className="text-gray-600">Sessions</span>
                    <span className="text-gray-900 font-medium">
                      {sessionsUsed.toLocaleString()} / {sessionsLimit.toLocaleString()}
                    </span>
                  </div>
                  <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                    <div
                      className={`h-full rounded-full ${sessionsPercent > 90 ? 'bg-red-500' : sessionsPercent > 70 ? 'bg-yellow-500' : 'bg-green-500'}`}
                      style={{ width: `${sessionsPercent}%` }}
                    />
                  </div>
                </div>
                <div>
                  <div className="flex justify-between text-sm mb-1">
                    <span className="text-gray-600">Events</span>
                    <span className="text-gray-900 font-medium">
                      {eventsUsed.toLocaleString()} / {eventsLimit.toLocaleString()}
                    </span>
                  </div>
                  <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                    <div
                      className={`h-full rounded-full ${eventsPercent > 90 ? 'bg-red-500' : eventsPercent > 70 ? 'bg-yellow-500' : 'bg-green-500'}`}
                      style={{ width: `${eventsPercent}%` }}
                    />
                  </div>
                </div>
                {(sessionsPercent > 80 || eventsPercent > 80) && (
                  <p className="text-sm text-yellow-700 bg-yellow-50 p-3 rounded-lg">
                    Running low on capacity? Upgrade your plan in Manage Subscription.
                  </p>
                )}
              </>
            )
          })()}
        </div>
      </section>

      {/* PAYMENT DISABLED - Subscription section hidden
      <section className="card mb-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Subscription</h2>
        <div className="grid gap-4">
          <div className="flex justify-between items-center">
            <div>
              <p className="text-sm text-gray-500">Status</p>
              <p className="font-medium capitalize">
                {profile?.subscription_status === 'trialing' && (
                  <span className="badge-warning">Trial</span>
                )}
                {profile?.subscription_status === 'active' && (
                  <span className="badge-success">Active</span>
                )}
                {profile?.subscription_status === 'canceled' && (
                  <span className="badge-gray">Canceled</span>
                )}
                {profile?.subscription_status === 'past_due' && (
                  <span className="badge-danger">Past Due</span>
                )}
              </p>
            </div>
            <div className="text-right">
              <p className="text-sm text-gray-500">Plan</p>
              <p className="font-medium">
                {((profile as any)?.subscription_quantity || 1) > 1
                  ? `Pro ${(profile as any)?.subscription_quantity}x - €${PRICING.monthlyPrice * ((profile as any)?.subscription_quantity || 1)}/month`
                  : `Pro - €${PRICING.monthlyPrice}/month`}
              </p>
            </div>
          </div>

          {profile?.subscription_status === 'trialing' && profile.trial_ends_at && (
            <div className="p-3 bg-yellow-50 rounded-lg text-sm text-yellow-800">
              Trial ends on {format(new Date(profile.trial_ends_at), 'MMMM d, yyyy')}
            </div>
          )}

          {profile?.subscription_status === 'active' && profile.current_period_end && (
            <p className="text-sm text-gray-600">
              Next billing date: {format(new Date(profile.current_period_end), 'MMMM d, yyyy')}
            </p>
          )}

          <button onClick={handleManageBilling} className="btn-secondary w-fit">
            Manage Subscription
          </button>
        </div>
      </section>
      */}

      {/* Account */}
      <section className="card mb-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Account</h2>
        <div className="grid gap-4">
          <div>
            <p className="text-sm text-gray-500">Email</p>
            <p className="text-gray-900">{profile?.email}</p>
          </div>
          <div>
            <p className="text-sm text-gray-500">Account Created</p>
            <p className="text-gray-900">
              {profile?.created_at && format(new Date(profile.created_at), 'MMMM d, yyyy')}
            </p>
          </div>
        </div>
      </section>

      {/* Self-Visit Exclusion */}
      <section className="card mb-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Exclude Your Visits</h2>
        <p className="text-sm text-gray-600 mb-4">
          Mark this browser to exclude your visits from dashboard statistics.
          Your visits will still be recorded but won't appear in metrics.
        </p>
        <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
          <div>
            <p className="font-medium text-gray-900">
              {isSelfExcluded ? 'Your visits are excluded' : 'Your visits are included'}
            </p>
            <p className="text-sm text-gray-500">
              {isSelfExcluded
                ? 'This browser is marked as belonging to you'
                : 'Your visits count toward dashboard statistics'}
            </p>
          </div>
          <button
            onClick={toggleSelfExclusion}
            className={`px-4 py-2 rounded-lg font-medium transition-colors ${
              isSelfExcluded
                ? 'bg-green-100 text-green-700 hover:bg-green-200'
                : 'bg-primary-100 text-primary-700 hover:bg-primary-200'
            }`}
          >
            {isSelfExcluded ? 'Include My Visits' : 'Exclude My Visits'}
          </button>
        </div>
        {isSelfExcluded && (
          <p className="text-xs text-gray-500 mt-3">
            Note: You'll need to mark each browser/device separately.
          </p>
        )}
      </section>

      {/* GDPR */}
      <section className="card">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Data & Privacy</h2>
        <p className="text-sm text-gray-600 mb-4">
          You can export or delete all your data at any time.
        </p>
        <div className="flex flex-wrap gap-3">
          <button onClick={handleExportData} className="btn-secondary">
            Export My Data
          </button>
          <button onClick={handleDeleteAccount} className="btn-danger">
            Delete My Account
          </button>
        </div>
      </section>
    </div>
  )
}
